<script setup lang="ts">
// ================================
// IMPORTS
// ================================
import { computed } from "vue"
import { useI18n } from "vue-i18n"
import { useConfigStore } from "@core/stores/config.store"
import { useResponsive } from "@core/composables"

// ================================
// INTERNATIONALIZATION
// ================================
const { t } = useI18n({
    messages: {
        fr: {
            welcome: "Bienvenue sur",
            step_information: "Informations",
            step_offer: "Offre & modules",
            step_sources: "Sources",
            step_finish: "Paiement",
        },
    },
})

// ================================
// PROPS & EMITS
// ================================
export interface SubscriptionFormHeaderModel {
    /** Numéro de l'étape en cours (0-based). */
    step: number
    /** True si l'étape courante est en erreur (ex. écran d'échec). */
    error?: boolean
}

const props = withDefaults(
    defineProps<{
        /** Étape en cours et statut d'erreur. */
        modelValue?: SubscriptionFormHeaderModel
    }>(),
    {
        modelValue: () => ({ step: 0, error: false }),
    }
)

// ================================
// COMPOSABLES
// ================================
const { isMobile } = useResponsive()
const configStore = useConfigStore()

// ================================
// COMPUTED
// ================================
/** Libellés des étapes (définis dans le header). */
const getStepLabels = computed(() => [
    t("step_information"),
    t("step_offer"),
    t("step_sources"),
    t("step_finish"),
])

const getLogo = computed(() => configStore.getScopeLogoFull)
const getScopeBrand = computed(
    () => configStore.getScopeBrand ?? configStore.getScopeName ?? "Homeys"
)

/** Étapes pour la timeline : statut dérivé de modelValue. */
const getTimelineItems = computed(() =>
    getStepLabels.value.map((label, index) => {
        const current = props.modelValue.step
        const isError = props.modelValue.error && index === current
        let status: "completed" | "current" | "disabled" | "error"
        if (index < current) status = "completed"
        else if (index === current) status = isError ? "error" : "current"
        else status = "disabled"
        return { label, status, stepNumber: index + 1 }
    })
)

/** Titre affiché (libellé de l'étape en cours). */
const getTitle = computed(
    () => getStepLabels.value[props.modelValue.step] ?? null
)

// ================================
// STATE (config affichage timeline)
// ================================
/** Couleurs des marqueurs par statut (fond + icône). */
const statusMarkerClass: Record<string, string> = {
    completed: "border-green-500 bg-green-500 text-white",
    current: "border-primary bg-primary text-white",
    error: "border-red-500 bg-red-500 text-white",
    disabled: "border-surface-300 bg-surface-100 text-surface-500",
}

/** Icônes PrimeIcons par statut (completed = check, error = times). */
const statusIcon: Record<string, string> = {
    completed: "pi pi-check",
    error: "pi pi-times",
}
</script>

<template>
    <BaseCard
        class="subscription-form-header"
        :pt="{
            content:
                'flex flex-col md:flex-row justify-between items-center gap-4 w-full px-6 md:px-10',
        }"
    >
        <template #content>
            <div class="flex flex-col items-center gap-4">
                <h2
                    class="text-xl font-semibold text-primary-900 text-center whitespace-nowrap"
                >
                    {{ t("welcome") }}
                </h2>
                <img
                    v-if="getLogo"
                    :src="getLogo"
                    :alt="getScopeBrand"
                    class="max-h-16 object-contain"
                />
            </div>
            <div class="flex flex-col items-center gap-4 md:w-[70%] w-full">
                <h1
                    v-if="getTitle"
                    class="text-2xl font-semibold text-primary-900 text-center flex items-center gap-2"
                >
                    {{ getTitle }}
                </h1>
                <BaseTimeline
                    v-if="getTimelineItems.length && !isMobile"
                    :value="getTimelineItems"
                    class="w-full"
                    layout="horizontal"
                    align="top"
                >
                    <template #marker="slotProps">
                        <span
                            :class="[
                                'min-w-10 min-h-10 max-w-10 max-h-10 flex items-center justify-center rounded-full border text-sm font-medium',
                                statusMarkerClass[slotProps.item.status] ??
                                    statusMarkerClass.disabled,
                            ]"
                        >
                            <i
                                v-if="statusIcon[slotProps.item.status]"
                                style="font-size: 1rem"
                                :class="[
                                    'text-white block',
                                    statusIcon[slotProps.item.status],
                                ]"
                            />
                            <span v-else class="leading-none">
                                {{ slotProps.item.stepNumber }}
                            </span>
                        </span>
                    </template>
                    <template #opposite="slotProps">
                        {{ slotProps.item.label }}
                    </template>
                </BaseTimeline>
            </div>
        </template>
    </BaseCard>
</template>
